import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

import getCookieToken from '@/lib/helpers/get-cookie-token';
import { toCamelCase } from '@/lib/helpers/to-camel-case';
import { toSnakeCase } from '@/lib/helpers/to-snake-case';

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// --- REQUEST INTERCEPTOR ---
axiosInstance.interceptors.request.use(
  function onRequestFulfilled(config: InternalAxiosRequestConfig) {
    const token = getCookieToken('XSRF_TOKEN');

    if (token) {
      config.headers['X-XSRF-TOKEN'] = token;
    }

    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = toSnakeCase(config.data);
    }

    if (config.params && typeof config.params === 'object') {
      config.params = toSnakeCase(config.params);
    }

    return config;
  },
  function onRequestRejected(error: AxiosError) {
    return Promise.reject(error);
  }
);

// --- RESPONSE INTERCEPTOR ---
axiosInstance.interceptors.response.use(
  function onResponseFulfilled(response: AxiosResponse) {
    if (response.data) {
      response.data = toCamelCase(response.data);
    }

    return response;
  },

  function onResponseRejected(error: AxiosError) {
    const { status, config } = error.response || {};
    const url = `${config?.baseURL ?? ''}${config?.url ?? ''}`;

    const isAuthCheck = url.includes('/users/me') && (status === 401 || status === 403);

    if (isAuthCheck) {
      return Promise.reject(error);
    }

    if (
      axios.isAxiosError(error) &&
      (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK')
    ) {
      return Promise.reject(new Error('network_interrupted'));
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
